import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/authSlice';
import { useAppSettings } from '../hooks/useAppSettings';
import api from '../services/api';

const navItems = [
  { path: '/dept/dashboard',       icon: '🏠', label: 'Dashboard' },
  { path: '/dept/applications',    icon: '📋', label: 'Applications' },
  { path: '/dept/notifications',   icon: '🔔', label: 'Notifications' },
  { path: '/dept/reports',         icon: '📊', label: 'Reports' },
  { path: '/dept/change-password', icon: '🔒', label: 'Change Password' },
];

export default function DeptLayout() {
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const { user, role } = useSelector(s => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useAppSettings();
  const handleLogout = () => { dispatch(logout()); navigate('/login'); };

  const headerColor = settings?.header_color || '#1a4731';
  const roleLabel   = role === 'final_dept' ? 'Final Approval Dept' : 'Related Department';

  // Load pending overdue count
  useEffect(() => {
    api.get('/reports/pending-overdue?days=7')
      .then(r => setPendingCount(r.applications?.length || 0))
      .catch(() => {});
  }, []);

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: settings?.bg_color || '#f7fafc' }}>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: `linear-gradient(180deg, ${headerColor} 0%, ${headerColor}cc 100%)` }}>
        <div className="flex flex-col h-full">

          {/* Logo */}
          <div className="px-6 py-5 border-b border-white/10" style={{ background: headerColor }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl">🏛️</div>
              <div>
                <p className="text-white font-bold text-sm truncate max-w-[140px]">
                  {settings?.app_name || 'Gov NOC Portal'}
                </p>
                <p className="text-white/70 text-xs">{roleLabel}</p>
              </div>
            </div>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-xs text-white/60">Officer</p>
            <p className="text-white font-medium text-sm truncate">{user?.name}</p>
            <p className="text-white/60 text-xs">{user?.dept_name}</p>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map(item => (
              <NavLink key={item.path} to={item.path}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}

            {/* Pending Alerts with badge */}
            <NavLink to="/dept/pending-alerts"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <span>⏰</span>
              <span className="flex-1">Pending Alerts</span>
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </NavLink>
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-white/10">
            <button onClick={handleLogout} className="sidebar-link w-full text-left">
              <span>🚪</span><span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4 shadow-sm">
          <button className="lg:hidden text-gray-500 text-xl" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <h2 className="text-gray-700 font-semibold text-sm flex-1">
            {settings?.app_name || 'Government NOC Management System'}
          </h2>

          {/* ── Pending Alert Bell ── */}
          <Link to="/dept/pending-alerts"
            className={`relative flex items-center justify-center w-9 h-9 rounded-xl transition-all
              ${pendingCount > 0
                ? 'bg-orange-50 border border-orange-300 hover:bg-orange-100'
                : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}>
            <span className="text-lg">⏰</span>
            {pendingCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {pendingCount > 9 ? '9+' : pendingCount}
              </span>
            )}
          </Link>

          <span className="text-xs bg-green-50 border border-green-200 text-green-700 px-3 py-1 rounded-full">
            {roleLabel}
          </span>

          {/* User dropdown */}
          <div className="relative">
            <button onClick={() => setDropdownOpen(p => !p)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-all">
              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:block max-w-[120px] truncate font-medium">{user?.name}</span>
              <span className="text-gray-400 text-xs">▾</span>
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-800 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-400">{roleLabel}</p>
                </div>
                <Link to="/dept/change-password"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all">
                  🔒 Change Password
                </Link>
                <button onClick={() => { setDropdownOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-all">
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6"><Outlet /></main>
      </div>
    </div>
  );
}